import {colorContrast} from "~/lib/helpers/color/color-contrast";
import {hexToRgb} from "~/lib/helpers/color/unit-conversion";
import {lightenDarken} from "~/lib/helpers/color/lighten-darken";
import {Customisation} from "~/lib/models/customisation";

export default function injectStyle(customisation: Customisation) {

  const primary = new InjectedColor("primary", customisation.colors.primary);
  const injectedStyle = `
      <style>
        :root {
           ${primary.toString()}
           ${signinPage(customisation.signinPage, primary)}
          --color-action: ${customisation.colors.action};
          --form-el-outline-color: #505050;
        }

        .background-primary {
          background-color: ${customisation.colors.primary};
        }

        .fill-primary {
          fill: ${customisation.colors.primary};
        }

        .step-tab {
          color: ${customisation.colors.action} !important;
          border-bottom-color: ${customisation.colors.action} !important;
        }

        .circle.selected {
          fill: ${customisation.colors.action} !important;
          stroke: ${customisation.colors.action} !important;
        }

        .outer-circle.selected {
          stroke: ${customisation.colors.action} !important;
          fill: transparent !important;
        }

        .background-action,
        .background-action:hover {
          color: ${customisation.colors.actionLabel} !important;
          background-color: ${customisation.colors.action} !important;
        }

        .background-action--white,
        .background-action--white:hover {
          color: #fff;
        }

        .background-action[disabled],
        .background-action[disabled]:hover {
          background-color: #848484;
        }

        .navigation-tabs li:not(.is-disabled):hover,
        .navigation-tabs li:not(.is-disabled):hover a,
        .navigation-tabs li.is-active,
        .navigation-tabs li.is-active a {
          color: ${customisation.colors.primary};
        }

        .section-title {
          color: ${customisation.colors.primary};
        }

        .navigation-tabs li.is-active {
          border-bottom-color: ${customisation.colors.primary};
        }

        svg .customizable-bg-primary,
        svg .customizable-bg {
          fill: ${customisation.colors.primary};
        }

        .hover-action.is-active,
        .hover-action:hover {
          color: ${customisation.colors.action};
        }

        .action svg .customizable-bg {
          fill: ${customisation.colors.action};
        }

        .map-player .workspace-sidebar  {
          background-color: ${customisation.colors.primary};
        }

        // .card-flip-details {
        //   background-color: ${customisation.colors.primary};
        // }

        .graph-cut {
          stroke: ${customisation.colors.primary};
          strokeWidth: 4px;
          fill: #fff;
        }

        .dragged .graph-cut {
          stroke: ${customisation.colors.primary};
          fill: ${customisation.colors.primary};
        }

        .stroke-action {
          stroke: ${customisation.colors.action};
        }

        .fill-action,
        .favorites-icon.active .icons_star_star {
          fill: ${customisation.colors.action};
        }

      </style>
    `;

  document
    .querySelector("head")
    .insertAdjacentHTML('beforeend', injectedStyle);
}

export class InjectedColor {

  private variableKey: string;
  private baseColor: string;
  private rgbComponents: { r: number, g: number, b: number };
  private rgbComponentsContrasted: { r: number, g: number, b: number };

  constructor(variableKey: string, baseColor: string) {
    this.baseColor = baseColor;
    this.variableKey = variableKey;
    this.rgbComponents = hexToRgb(baseColor);
    this.rgbComponentsContrasted = colorContrast(this.rgbComponents);
  }

  get rgbParams() {
    return this.rgbComponents;
  }

  get rgb() {
    return toRGBString(this.rgbComponents);
  }

  get contrasted() {
    return toRGBString(this.rgbComponentsContrasted);
  }

  get extraShade() {
    return toRGBString(lightenDarken(this.rgbComponents, -30));
  }
  
  public toRGBString(color, alpha = 1) {
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
  }

  toEntries() {
    return [
      [`--color-${this.variableKey}-tint`, this.toRGBString(lightenDarken(this.rgbComponents, 20))],
      [`--color-${this.variableKey}`, this.toRGBString(this.rgbComponents)],
      [`--color-${this.variableKey}-shade`, this.toRGBString(lightenDarken(this.rgbComponents, -20))],
      [`--color-${this.variableKey}-extra-shade`, this.toRGBString(lightenDarken(this.rgbComponents, -30))],
      [`--color-${this.variableKey}-alpha-01`, this.toRGBString(this.rgbComponents, 0.05)],
      [`--color-${this.variableKey}-alpha-02`, this.toRGBString(this.rgbComponents, 0.1)],
      [`--color-${this.variableKey}-alpha-03`, this.toRGBString(this.rgbComponents, 0.2)],
      [`--color-${this.variableKey}-alpha-50`, this.toRGBString(this.rgbComponents, 0.5)],
      [`--color-${this.variableKey}-alpha-70`, this.toRGBString(this.rgbComponents, 0.7)],
      [`--color-${this.variableKey}-rgb`, `${this.rgbComponents.r},${this.rgbComponents.g},${this.rgbComponents.b}`],
      [`--color-${this.variableKey}-tint-contrasted`, this.toRGBString(lightenDarken(this.rgbComponentsContrasted, 20))],
      [`--color-${this.variableKey}-contrasted`, this.toRGBString(this.rgbComponentsContrasted)],
      [`--color-${this.variableKey}-shade-contrasted`, this.toRGBString(lightenDarken(this.rgbComponentsContrasted, -20))],
      [`--color-${this.variableKey}-rgb-contrasted`, `${this.rgbComponentsContrasted.r},${this.rgbComponentsContrasted.g},${this.rgbComponentsContrasted.b}`],
    ];
  }

  toString() {
    return this.toEntries().map(([key, value]) => `${key}: ${value};`).join("\n");
  }

}

function signinPage(customisation, fallback: InjectedColor) {

  const paramOrFallback = (param, fallback) => param ? toRGBString(hexToRgb(param)) : fallback;
  const overlay = paramOrFallback(customisation?.background?.overlay, "255, 255, 255");
  const overlayOpacity = customisation?.background?.overlayOpacity || 0.9;
  const text = paramOrFallback(customisation?.text, fallback.extraShade);
  const buttonColor = paramOrFallback(customisation?.button?.color, fallback.contrasted);
  const buttonBackground = paramOrFallback(customisation?.button?.background, fallback.extraShade);

  const frameStrip = toRGBString(lightenDarken(fallback.rgbParams, 100));
  const frameBackground = toRGBString(lightenDarken(fallback.rgbParams,150));
  return `
    --color-signin-overlay: ${overlay};
    --color-signin-overlay-opacity: ${overlayOpacity};
    --color-signin-text: ${text};
    --color-signin-button-color: ${buttonColor};
    --color-signin-button-background: ${buttonBackground};
    --color-signin-frame-background: ${frameBackground};
    --color-signin-frame-strip: ${frameStrip};
  `;

}

function toRGBString({r, g, b}) {
  return `${r},${g},${b}`
}
